import React from 'react';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
  SCHEMA_TYPE_LOCATION,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
  composeValidators,
  autocompletePlaceSelected,
} from '../../util/validators';
// Import shared components
import {
  FieldCheckboxGroup,
  FieldSelect,
  FieldTextInput,
  FieldBoolean,
  FieldLocationAutocompleteInput,
  FieldCheckbox,
} from '../../components';

// Identity function for location field
const identity = v => v;
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const messageOrString = (intl, messageIdOrString) => {
  return typeof messageIdOrString === 'string'
    ? intl.formatMessage({ id: messageIdOrString, defaultMessage: messageIdOrString })
    : '';
};

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const requiredMsg = requiredMessage
    ? messageOrString(intl, requiredMessage)
    : defaultRequiredMessage;
  const validateMaybe = isRequired ? { validate: required(requiredMsg) } : {};
  const placeholder = placeholderMessage
    ? messageOrString(intl, placeholderMessage)
    : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const filterOptions = createFilterOptions(enumOptions).map(o => ({
    ...o,
    label: messageOrString(intl, o.label),
  }));

  const label = messageOrString(intl, getLabel(fieldConfig));

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      parse={value => value === '' ? null : value}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = messageOrString(intl, getLabel(fieldConfig));
  const requiredMsg = requiredMessage
    ? messageOrString(intl, requiredMessage)
    : defaultRequiredMessage;
  const validateMaybe = isRequired ? { validate: nonEmptyArray(requiredMsg) } : {};

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(enumOptions).map(o => ({
        ...o,
        label: messageOrString(intl, o.label),
      }))}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage, validate: customValidate } =
    fieldConfig?.saveConfig || {};
  const label = messageOrString(intl, getLabel(fieldConfig));
  const requiredMsg = requiredMessage
    ? messageOrString(intl, requiredMessage)
    : defaultRequiredMessage;

  const translatedCustomValidate = customValidate
    ? (value, allValues, meta) => {
        const errorSrc = customValidate(value, allValues, meta);
        return errorSrc ? messageOrString(intl, errorSrc) : undefined;
      }
    : undefined;

  const validateMaybe = {};
  if (isRequired && translatedCustomValidate) {
    validateMaybe.validate = composeValidators(required(requiredMsg), translatedCustomValidate);
  } else if (isRequired) {
    validateMaybe.validate = required(requiredMsg);
  } else if (translatedCustomValidate) {
    validateMaybe.validate = translatedCustomValidate;
  }

  const placeholder = placeholderMessage
    ? messageOrString(intl, placeholderMessage)
    : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  // Add extra top padding for postal code field
  const fieldKey = fieldConfig?.key || '';
  const fieldClassName =
    fieldKey === 'postalCode' ? `${css.customField} ${css.postalCodeField}` : css.customField;

  return (
    <FieldTextInput
      className={fieldClassName}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = messageOrString(intl, getLabel(fieldConfig));
  const placeholder = placeholderMessage
    ? messageOrString(intl, placeholderMessage)
    : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage
      ? messageOrString(intl, requiredMessage)
      : defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = messageOrString(intl, getLabel(fieldConfig));
  const requiredMsg = requiredMessage
    ? messageOrString(intl, requiredMessage)
    : defaultRequiredMessage;

  // Validation for checkbox (must be checked if required)
  // For checkboxes, "required" usually means "must be true"
  const validateMaybe = isRequired ? { validate: required(requiredMsg) } : {};

  // Use generic FieldBoolean (Select Yes/No) by default
  // But use Checkbox for specific agreement/consent fields
  const checkboxKeys = [
    'voluntaryAgreement',
    'collaborationConditions',
    'subscriptionAgreement',
    'allowFarmVisits',
  ];
  const isCheckbox = checkboxKeys.includes(fieldConfig?.key);

  if (isCheckbox) {
    // For standalone boolean checkboxes, we need to handle the value properly
    // Don't pass a value prop - let the checkbox store true/false directly
    return (
      <FieldCheckbox
        className={css.customField}
        id={formId ? `${formId}.${name}` : name}
        name={name}
        label={label}
        // For boolean checkboxes, Final Form stores true/false automatically
        // Don't pass value prop here
        {...validateMaybe}
      />
    );
  }

  const placeholder = placeholderMessage
    ? messageOrString(intl, placeholderMessage)
    : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = messageOrString(intl, getLabel(fieldConfig));
  const placeholder = placeholderMessage
    ? messageOrString(intl, placeholderMessage)
    : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage
      ? messageOrString(intl, requiredMessage)
      : defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomFieldLocation = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = messageOrString(intl, getLabel(fieldConfig));
  const requiredMsg = requiredMessage
    ? messageOrString(intl, requiredMessage)
    : defaultRequiredMessage;
  const validateMaybe = isRequired ? { validate: autocompletePlaceSelected(requiredMsg) } : {};
  const placeholder = placeholderMessage
    ? messageOrString(intl, placeholderMessage)
    : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLocation' });

  return (
    <div>
      <FieldLocationAutocompleteInput
        rootClassName={css.locationField}
        inputClassName={css.locationInput}
        iconClassName={css.locationIcon}
        useDarkText={false}
        id={formId ? `${formId}.${name}` : name}
        name={name}
        label={label}
        placeholder={placeholder}
        format={identity}
        useDefaultPredictions={false}
        {...validateMaybe}
      />
    </div>
  );
};

const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, props)
    : schemaType === SCHEMA_TYPE_LOCATION
    ? renderFieldComponent(CustomFieldLocation, props)
    : null;
};

export default CustomExtendedDataField;
